import React, { useEffect, useState, useRef } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Clock, AlertCircle, FileCheck, ArrowRight, Send, Download, FileText, MessageSquare, Briefcase } from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';

// Status configuration
const STATUS_CONFIG = {
  SUBMITTED: {
    label: 'Submitted',
    color: 'success',
    badgeClass: 'bg-green-500',
    borderClass: 'border-l-green-500',
    icon: CheckCircle2,
  },
  NEED_INFO: {
    label: 'Waiting for Info',
    color: 'warning',
    badgeClass: 'bg-yellow-500',
    borderClass: 'border-l-yellow-500',
    icon: AlertCircle,
  },
  READY_FOR_REVIEW: {
    label: 'Ready for Review',
    color: 'success',
    badgeClass: 'bg-green-500',
    borderClass: 'border-l-green-500',
    icon: FileCheck,
  },
  SUBMITTED_TO_IRS: {
    label: 'Submitted to IRS',
    color: 'success',
    badgeClass: 'bg-green-600',
    borderClass: 'border-l-green-600',
    icon: Send,
  },
  DRAFT: {
    label: 'Draft',
    color: 'muted',
    badgeClass: 'bg-gray-500',
    borderClass: 'border-l-gray-500',
    icon: Clock,
  },
};

// Timeline steps order
const TIMELINE_STEPS = ['SUBMITTED', 'NEED_INFO', 'READY_FOR_REVIEW', 'SUBMITTED_TO_IRS'];

export default function BusinessFilingStatusPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFiling, setCurrentFiling] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to latest message within container
  const scrollToBottom = () => {
    const scrollArea = messagesEndRef.current?.closest('[data-radix-scroll-area-viewport]');
    if (scrollArea) {
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages]);

  // Fetch messages for current filing
  const fetchMessages = async (filingId) => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${backendUrl}/api/filings/${filingId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Send a message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !currentFiling) return;

    try {
      setSendingMessage(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${backendUrl}/api/filings/${currentFiling.id}/messages`,
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Poll for new messages every 10 seconds when filing is not DRAFT
  useEffect(() => {
    if (currentFiling && currentFiling.status !== 'DRAFT') {
      fetchMessages(currentFiling.id);
      
      const interval = setInterval(() => {
        fetchMessages(currentFiling.id);
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [currentFiling]);

  useEffect(() => {
    const fetchFilings = async () => {
      try {
        setLoading(true);
        const backendUrl = getBackendUrl();
        const token = localStorage.getItem('access_token');
        
        const response = await axios.get(`${backendUrl}/api/tax-filings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const data = response.data || [];
        // Filter for business filings only
        const businessFilings = data.filter(f => f.filing_type === 'business');
        setFilings(businessFilings);
        
        // Set the first non-draft filing as current, or first filing if all are drafts
        if (businessFilings.length > 0) {
          const nonDraftFilings = businessFilings.filter(f => f.status !== 'DRAFT');
          if (nonDraftFilings.length > 0) {
            setCurrentFiling(nonDraftFilings[0]);
          } else {
            setCurrentFiling(businessFilings[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching filings:', error);
        toast.error('Failed to load filings');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchFilings();
    }
  }, [user]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get step status based on current filing status
  const getStepStatus = (stepKey, currentStatus) => {
    const currentIndex = TIMELINE_STEPS.indexOf(currentStatus);
    const stepIndex = TIMELINE_STEPS.indexOf(stepKey);
    
    // Special case: SUBMITTED step is always completed once filing is submitted
    if (stepKey === 'SUBMITTED' && currentIndex >= 0) {
      return 'completed';
    }
    
    if (stepIndex < currentIndex) {
      return 'completed';
    } else if (stepIndex === currentIndex) {
      return 'current';
    } else {
      return 'pending';
    }
  };

  // Get timestamp for a step
  const getStepTimestamp = (stepKey, filing) => {
    if (!filing) return null;
    
    switch (stepKey) {
      case 'SUBMITTED':
        return filing.submitted_at;
      case 'NEED_INFO':
        return filing.need_info_at;
      case 'READY_FOR_REVIEW':
        return filing.ready_for_review_at;
      case 'SUBMITTED_TO_IRS':
        return filing.submitted_to_irs_at;
      default:
        return null;
    }
  };

  // Build timeline steps from current filing
  const buildTimelineSteps = () => {
    if (!currentFiling) return [];
    
    const currentStatus = currentFiling.status;
    
    return TIMELINE_STEPS.map((stepKey) => {
      const config = STATUS_CONFIG[stepKey];
      const status = getStepStatus(stepKey, currentStatus);
      const timestamp = getStepTimestamp(stepKey, currentFiling);
      
      return {
        key: stepKey,
        label: config.label,
        status,
        timestamp,
        icon: config.icon,
        showMessage: stepKey === 'NEED_INFO' && status === 'current',
        message: currentFiling.status_message,
      };
    });
  };

  // Download agent document
  const handleDownloadDocument = async (doc) => {
    try {
      const backendUrl = getBackendUrl();
      const downloadUrl = `${backendUrl}${doc.path}`;

      // For static files, use direct download approach
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = doc.name;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  // Handle filing selection from dropdown
  const handleFilingChange = (filingId) => {
    const selected = filings.find(f => f.id === filingId);
    if (selected) {
      setCurrentFiling(selected);
      setMessages([]); // Clear messages when switching filings
    }
  };

  // Get business name for display
  const getBusinessName = (filing) => {
    return filing.personal_info?.business_name || 'Unnamed Business';
  };

  const timelineSteps = buildTimelineSteps();
  const currentStatusConfig = currentFiling ? STATUS_CONFIG[currentFiling.status] || STATUS_CONFIG.DRAFT : null;

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Business Filing Status" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  // No filings yet
  if (filings.length === 0) {
    return (
      <DashboardLayout role={user.role} title="Business Filing Status" user={user}>
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Business Filing Status</h2>
            <p className="mt-1 text-muted-foreground">Track your business tax filing progress</p>
          </div>
          
          <Card className="card-elevated">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Briefcase className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Business Filings Yet</h3>
                <p className="text-muted-foreground mb-4">
                  You haven't submitted any business tax filings yet. Start a new filing to track its progress here.
                </p>
                <Button onClick={() => navigate('/business/filing/start?new=true')} className="gap-2">
                  Start New Business Filing
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="Business Filing Status" user={user}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Business Filing Status</h2>
          <p className="mt-1 text-muted-foreground">Track your business tax filing progress</p>
        </div>

        {/* Filing Selector Dropdown */}
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Briefcase className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Select Business Filing</label>
                <Select value={currentFiling?.id} onValueChange={handleFilingChange}>
                  <SelectTrigger className="w-full md:w-[400px]">
                    <SelectValue placeholder="Select a business filing" />
                  </SelectTrigger>
                  <SelectContent>
                    {filings.filter(f => f.status !== 'DRAFT').map((filing) => (
                      <SelectItem key={filing.id} value={filing.id}>
                        {getBusinessName(filing)} - Tax Year {filing.tax_year} ({filing.status?.replace('_', ' ')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {currentFiling && (
          <>
            {/* Current Status Card */}
            <Card className={cn("card-elevated border-l-4", currentStatusConfig?.borderClass)}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Current Status</p>
                    <p className="mt-1 text-2xl font-bold text-foreground">
                      {currentStatusConfig?.label}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {getBusinessName(currentFiling)} • Tax Year {currentFiling.tax_year}
                    </p>
                    
                    {/* Agent Documents - Show all documents from agent */}
                    {currentFiling.status !== 'DRAFT' && currentFiling.agent_documents && currentFiling.agent_documents.length > 0 && (
                      <div className="mt-4 space-y-3">
                        <p className="text-sm font-medium text-green-700 dark:text-green-300">
                          Documents from your Tax Agent ({currentFiling.agent_documents.length}):
                        </p>
                        <div className="space-y-2">
                          {currentFiling.agent_documents.map((doc, index) => (
                            <div
                              key={doc.id || index}
                              className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
                                <div>
                                  <p className="text-sm font-medium">{doc.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {doc.uploaded_at
                                      ? new Date(doc.uploaded_at).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                        })
                                      : ''}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-2"
                                onClick={() => handleDownloadDocument(doc)}
                              >
                                <Download className="h-4 w-4" />
                                Download
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Message Box for all non-draft statuses */}
                    {currentFiling.status !== 'DRAFT' && (
                      <div className="mt-4 space-y-3">
                        <div className="border rounded-lg bg-muted/20">
                          <div className="p-3 border-b bg-muted/50">
                            <div className="flex items-center gap-2">
                              <MessageSquare className="h-4 w-4" />
                              <span className="font-medium text-sm">Conversation with Agent</span>
                            </div>
                          </div>
                          <ScrollArea className="h-64 p-3">
                            <div className="space-y-3">
                              {messages.length === 0 && (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                  No messages yet. Start a conversation with your agent.
                                </p>
                              )}
                              {messages.map((msg) => (
                                <div
                                  key={msg.id}
                                  className={cn(
                                    "flex",
                                    msg.sender_role === 'client' ? 'justify-end' : 'justify-start'
                                  )}
                                >
                                  <div
                                    className={cn(
                                      "max-w-[80%] rounded-lg p-3",
                                      msg.sender_role === 'client'
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted'
                                    )}
                                  >
                                    <p className="text-xs font-medium mb-1 opacity-70">
                                      {msg.sender_role === 'client' ? 'You' : msg.sender_name || 'Agent'}
                                    </p>
                                    <p className="text-sm">{msg.message}</p>
                                    <p className="text-xs mt-1 opacity-50">
                                      {new Date(msg.created_at).toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </p>
                                  </div>
                                </div>
                              ))}
                              <div ref={messagesEndRef} />
                            </div>
                          </ScrollArea>
                          <div className="p-3 border-t">
                            <div className="flex gap-2">
                              <Input
                                placeholder="Type your message..."
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                disabled={sendingMessage}
                              />
                              <Button 
                                onClick={handleSendMessage}
                                disabled={!newMessage.trim() || sendingMessage}
                                className="gap-2"
                              >
                                <Send className="h-4 w-4" />
                                Send
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {['DRAFT', 'SUBMITTED', 'NEED_INFO'].includes(currentFiling.status) && (
                      <Button 
                        className="mt-4 gap-2"
                        onClick={() => navigate(`/business/filing/start?id=${currentFiling.id}`)}
                      >
                        {currentFiling.status === 'DRAFT' ? 'Continue Filing' : 'Edit Filing'}
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <Badge className={cn(currentStatusConfig?.badgeClass, "text-white")}>
                    {currentFiling.status === 'NEED_INFO' ? 'Action Required' : currentStatusConfig?.label}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Your Tax Professional - Show when agent is assigned */}
            {currentFiling.agent_name && (
              <Card className="card-elevated border-blue-200 dark:border-blue-800 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center text-white text-lg font-semibold shadow-lg">
                      {currentFiling.agent_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Your Tax Professional</p>
                      <p className="text-xl font-bold text-blue-900 dark:text-blue-100">{currentFiling.agent_name}</p>
                      {currentFiling.agent_email && (
                        <p className="text-sm text-muted-foreground">{currentFiling.agent_email}</p>
                      )}
                    </div>
                    <Button 
                      variant="outline" 
                      className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300"
                      onClick={() => {
                        const chatSection = document.getElementById('chat-section');
                        if (chatSection) chatSection.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Message
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Timeline - Only show for non-draft filings */}
            {currentFiling.status !== 'DRAFT' && (
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle>Filing Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-8">
                    {timelineSteps.map((step, index) => {
                      const Icon = step.icon;
                      const isCompleted = step.status === 'completed';
                      const isCurrent = step.status === 'current';
                      const isPending = step.status === 'pending';
                      
                      return (
                        <div key={step.key} className="relative flex gap-4">
                          {index < timelineSteps.length - 1 && (
                            <div
                              className={cn(
                                'absolute left-4 top-10 h-full w-0.5',
                                isCompleted ? 'bg-green-500' : 'bg-border'
                              )}
                            />
                          )}

                          <div
                            className={cn(
                              'relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2',
                              isCompleted && 'border-green-500 bg-green-500',
                              isCurrent && 'border-yellow-500 bg-yellow-500',
                              isPending && 'border-gray-300 bg-background'
                            )}
                          >
                            <Icon
                              className={cn(
                                'h-4 w-4',
                                (isCompleted || isCurrent) ? 'text-white' : 'text-muted-foreground'
                              )}
                            />
                          </div>

                          <div className="flex-1 pt-0.5">
                            <div className="flex items-center justify-between">
                              <p
                                className={cn(
                                  'font-medium',
                                  isPending ? 'text-muted-foreground' : 'text-foreground'
                                )}
                              >
                                {step.label}
                              </p>
                              <span className="text-sm text-muted-foreground">
                                {step.timestamp ? formatDate(step.timestamp) : (isPending ? 'Pending' : '')}
                              </span>
                            </div>
                            
                            {step.showMessage && step.message && (
                              <div className="mt-2">
                                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                    {step.message}
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {isCurrent && step.key === 'NEED_INFO' && !step.message && (
                              <div className="mt-2">
                                <p className="text-sm text-muted-foreground">
                                  Additional information is needed to proceed with your filing. Use the message box above to communicate with your agent.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Filing Details */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>Filing Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Business Name</span>
                    <span className="font-medium">{getBusinessName(currentFiling)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Tax Year</span>
                    <span className="font-medium">{currentFiling.tax_year}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Filing Type</span>
                    <span className="font-medium capitalize">{currentFiling.filing_type}</span>
                  </div>
                  {currentFiling.estimated_refund && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Estimated Tax</span>
                      <span className="font-medium text-green-600">
                        ${currentFiling.estimated_refund.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {currentFiling.balance_due && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Balance Due</span>
                      <span className="font-medium text-red-600">
                        ${currentFiling.balance_due.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Created</span>
                    <span className="font-medium">{formatDate(currentFiling.created_at)}</span>
                  </div>
                  {currentFiling.submitted_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Submitted</span>
                      <span className="font-medium">{formatDate(currentFiling.submitted_at)}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
